import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";
import { withFollow } from "./followHelpers";

export const listCreditAccountsSkill: Skill = {
  id: "listCreditAccounts",
  description:
    "Lista las cuentas de crédito de clientes (clientes con deuda pendiente).",
  schema: {
    type: "object",
    properties: { restaurantId: { type: "string" } },
    required: ["restaurantId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    if (!restaurantId) return { ok: false, error: "Falta restaurantId." };
    return withFollow(ctx, {
      intro: "Listando cuentas de crédito…",
      successMsg: "✓ Cuentas obtenidas",
      run: async () => {
        try {
          const { data } = await apiWithAuth.get(
            `/restaurants/${restaurantId}/credit-accounts`,
            ctx.accessToken,
          );
          const items: any[] = data?.items ?? data?.data ?? data ?? [];
          return {
            ok: true as const,
            result: {
              count: items.length,
              accounts: items.slice(0, 30).map((a) => ({
                clientId: a.clientId,
                clientName: a.client?.name,
                balance: a.balance,
                limit: a.limit,
              })),
            },
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const payCreditAccountSkill: Skill = {
  id: "payCreditAccount",
  description: "Registra un abono / pago a la cuenta de crédito de un cliente.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      clientId: { type: "string" },
      amount: { type: "number" },
      paymentMethod: { type: "string", enum: ["cash", "card", "transfer"] },
      paymentDetails: { type: "string" },
      cashRegisterId: { type: "string" },
      notes: { type: "string" },
    },
    required: ["restaurantId", "clientId", "amount", "paymentMethod"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const clientId = String(args.clientId ?? "");
    const amount = Number(args.amount);
    const paymentMethod = String(args.paymentMethod ?? "");
    if (!restaurantId || !clientId || !Number.isFinite(amount) || amount <= 0 || !paymentMethod) {
      return { ok: false, error: "Datos inválidos." };
    }
    const paymentDetails = args.paymentDetails ? String(args.paymentDetails) : undefined;
    const cashRegisterId = args.cashRegisterId ? String(args.cashRegisterId) : undefined;
    const notes = args.notes ? String(args.notes) : undefined;

    const ok = await ctx.confirmation.confirm({
      title: "Registrar abono a crédito",
      description: "Vas a registrar un pago contra la cuenta de crédito del cliente.",
      details: [
        { label: "Monto", value: `$${amount.toLocaleString()}` },
        { label: "Método", value: paymentMethod },
        ...(paymentDetails ? [{ label: "Detalle", value: paymentDetails }] : []),
      ],
      confirmLabel: "Registrar abono",
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: `Registrando abono $${amount.toLocaleString()}…`,
      successMsg: "✓ Abono registrado",
      restaurantId,
      targetTab: "cuentas-credito",
      targetTabLabel: "Cuentas de crédito",
      run: async () => {
        try {
          const body: Record<string, unknown> = { amount, paymentMethod };
          if (paymentDetails) body.paymentDetails = paymentDetails;
          if (cashRegisterId) body.cashRegisterId = cashRegisterId;
          if (notes) body.notes = notes;
          const { data } = await apiWithAuth.post(
            `/restaurants/${restaurantId}/credit-accounts/${clientId}/pay`,
            body,
            ctx.accessToken,
          );
          return {
            ok: true as const,
            result: { clientId, amount, newBalance: data?.balance },
            humanSummary: `Abono de $${amount.toLocaleString()} registrado.`,
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};
